<template>
    <GgmsModal :open="isCreateTaskModalOpen" @closeModal="closeCreateTaskModal" @keydown.enter.prevent="handleEnterKey">
        <template #header>
            <div class="flex justify-flex-start items-center">
                <div class="mr-4 flex items-center justify-center h-12 w-12 rounded-full bg-primary-color-100">
                    <ClipboardCheckIcon class="h-6 w-6 text-primary-color-600" aria-hidden="true" />
                </div>
                <DialogTitle as="h3" class="text-lg leading-6 font-medium text-gray-900"
                    >{{ modalHeaderText }} Task
                </DialogTitle>
            </div>
        </template>

        <CreateTaskForm
            v-if="isCreateTaskModalOpen"
            :errorValue="error"
            :isForWorkflow="false"
            :modalAction="modalAction"
            :isCreateTaskModalOpen="isCreateTaskModalOpen"
            :isUpdateTask="!isModalActionCreate"
            @update:task="taskUpdated"
            :taskId="taskId"
        />
        <template #under>
            <div class="flex justify-between">
                <div>
                    <GgmsButton
                        v-if="!isModalActionCreate"
                        styleType="active"
                        :disabled="isNotEditableByUser"
                        :classes="[
                            'text-red-600 bg-transparent border-transparent shadow-transparent focus:ring-red-600/50',
                        ]"
                        @click="deleteTaskFromModal()"
                        >Delete
                    </GgmsButton>
                </div>
                <div class="flex">
                    <GgmsButton styleType="secondary" @click="closeCreateTaskModal">Cancel</GgmsButton>
                    <GgmsButton class="ml-3" :disabled="isSubmitting || isNotEditableByUser" @click="addTask">
                        {{ actionButtonText }}
                    </GgmsButton>
                </div>
            </div>
        </template>
    </GgmsModal>
</template>
<script lang="ts" setup>
import { computed, ref, reactive, watch, onMounted, nextTick } from "vue"
import GgmsModal from "@/components/GgmsModal.vue"
import GgmsButton from "@/components/GgmsButton.vue"
import { DialogTitle } from "@headlessui/vue"
import { ClipboardCheckIcon } from "@heroicons/vue/outline"
import { useTaskStore } from "@/stores/task"
import { useAgentsStore } from "@/stores/agents"
import { useGridCollectionStore } from "@/stores/grid-collection"
import { useAuthStore } from "@/stores/auth"
import { useAgencyStore } from "@/stores/agency"
import { ToastService } from "@/shared/services/toast"

import { deleteEmptyKeys, isAdmin, getDomainId, getTaskTypes, getTaskReminderOptions } from "@/shared/utils/helpers"
import { formatters, getters } from "@/shared/utils/dateUtils"

import { Task } from "@/shared/models/task"

import CreateTaskForm from "@/components/tasks/CreateTaskForm.vue"

const toastService = new ToastService()
const taskStore = useTaskStore()
const agentStore = useAgentsStore()
const authStore = useAuthStore()
const agencyStore = useAgencyStore()
const gridCollectionStore = useGridCollectionStore()

interface Props {
    isCreateTaskModalOpen: boolean
    modalAction: string
    taskId: string
    contactId?: string
    isNotEditableByUser: boolean
}

const props = defineProps<Props>()
const emit = defineEmits(["onDeleteTask", "onCreateTaskModalClose", "onCreateTask"])

const error = ref()
const isTaskLoading = ref(false)
const queueDate = ref("")
const queueTime = ref("")

const taskTypes = ref(getTaskTypes())
const reminderOptions = ref(getTaskReminderOptions())

const cachedAgents = computed(() => agentStore.tableState.data)
const currentUser = computed(() => authStore.currentUser)
const isModalActionCreate = computed(() => props.modalAction === "create")
const actionButtonText = computed(() => {
    if (!isSubmitting.value) {
        return isModalActionCreate.value ? "Create task" : "Save"
    }
    return isModalActionCreate.value ? "Creating task..." : "Saving task..."
})
const modalHeaderText = computed(() => (isModalActionCreate.value ? "Create" : "Edit"))

const isSubmitting = ref(false)

const task = reactive({
    _id: "",
    displayName: "",
    description: "",
    when: {
        startAt: "",
        timezone: "America/Los_Angeles",
    },
    reminder: reminderOptions.value[0].attribute,
    shouldMarkAsMissedIfNotCompleted: true,
    agent: {
        _id: currentUser?.value?._id,
        fullName: currentUser?.value?.fullName,
    },
    contactId: "",
    agentId: currentUser?.value?._id,
    collection: {
        _id: "",
        displayName: "",
    },
    type: "",
    collectionId: "",
    status: "",
    notes: "",
    domainId: "",
    unitsOfTimeToRemove: "30",
    typeOfTimeToRemove: "days",
})

const domainOptions = computed(() => agencyStore.agency.agencyDomains)
const selectedDomain = ref(initializeDomain())

function handleEnterKey(event: KeyboardEvent) {
    if (event.key === "Enter" && props.isCreateTaskModalOpen) {
        addTask()
    }
}

function resetTaskObject() {
    task._id = ""
    task.displayName = ""
    task.description = ""
    task.when = {
        startAt: "",
        timezone: "America/Los_Angeles",
    }
    task.reminder = reminderOptions.value[0].attribute
    task.shouldMarkAsMissedIfNotCompleted = true
    task.agent = {
        _id: currentUser.value._id,
        fullName: currentUser.value.fullName,
    }
    task.contactId = ""
    task.agentId = currentUser.value._id
    task.type = ""
    task.collection = {
        _id: "",
        displayName: "",
    }
    task.collectionId = ""
    task.status = ""
    task.notes = ""
    selectedDomain.value = initializeDomain()
    setQueueDateAndTime(new Date())
}

function taskUpdated(updatedTask: Task) {
    Object.assign(task, updatedTask)
}

function closeCreateTaskModal() {
    error.value = null
    resetTaskObject()
    emit("onCreateTaskModalClose", false)
}

function setQueueDateAndTime(date: Date) {
    queueDate.value = getters.getDateInputFormat(date)
    queueTime.value = getters.getTimeInputFormat(date)
}

async function validateTask(task: Task) {
    const isPastTime = checkIfQueueTimeIsInPast(task.when.startAt)
    if (isPastTime) {
        await nextTick(() => {
            error.value = {
                field: "when.startat",
                message: "Task time cannot be in the past.",
            }
        })
        isSubmitting.value = false
        return false
    }
    // setTaskStartDate()

    if (!task.domainId) {
        await nextTick(() => {
            error.value = {
                field: "domain",
                message: "Domain is required. Please select a domain.",
            }
        })
        isSubmitting.value = false
        return false
    }
    return true
}

async function addTask() {
    const isCreateTask = props.modalAction === "create"
    isSubmitting.value = true
    try {
        error.value = undefined

        const nonEmptyObj = deleteEmptyKeys(task)
        if (props.contactId) {
            nonEmptyObj.contactId = props.contactId
        }

        let toastMessage = ""
        if (await validateTask(nonEmptyObj)) {
            // Convert when.startAt from agency's timezone to UTC
            if (nonEmptyObj.when && nonEmptyObj.when.startAt) {
                nonEmptyObj.when.startAt = formatters.formatDateToUtc(nonEmptyObj.when.startAt)
            }

            if (isCreateTask) {
                localStorage.setItem("taskDomainId", task.domainId ?? selectedDomain.value)
                await taskStore.createTask(nonEmptyObj)
                toastMessage = "A new task was created."
                localStorage.removeItem("taskDomainId")
            } else {
                nonEmptyObj.description = task.description ?? ""
                nonEmptyObj.notes = task.notes ?? ""
                await taskStore.updateTask(props.taskId, nonEmptyObj)
                toastMessage = "Task was successfully updated."
            }

            toastService.addToast({
                type: "success",
                message: toastMessage,
            })

            emit("onCreateTask")

            gridCollectionStore.collectionType = "task"
            await Promise.all([taskStore.getActiveTaskSession(), gridCollectionStore.getCollections()])

            closeCreateTaskModal()
        }
    } catch (err) {
        error.value = err
        localStorage.removeItem("taskDomainId")
    }
    isSubmitting.value = false
}

async function deleteTaskFromModal() {
    await taskStore.deleteOne(props.taskId)

    toastService.addToast({
        type: "success",
        message: "Task was deleted.",
    })

    closeCreateTaskModal()
    taskStore.getTasks()
    emit("onDeleteTask")
}

function initializeDomain() {
    const domain = getDomainId()
    return domain === "all" ? null : domain
}

function checkIfQueueTimeIsInPast(dateString: string) {
    const taskDate = getters.getNewDate(dateString)
    const now = new Date()
    return taskDate < now
}

onMounted(async () => {
    gridCollectionStore.collectionType = "task"
    if (isAdmin() && !cachedAgents.value.length) {
        await agentStore.loadAgents()
    }
    setQueueDateAndTime(new Date())
})

watch(
    () => props.isCreateTaskModalOpen,
    async () => {
        if (props.modalAction === "edit" && props.isCreateTaskModalOpen) {
            try {
                isTaskLoading.value = true
                const response = await taskStore.getTask(props.taskId)
                task.displayName = response.task.displayName
                task.description = response.task.description
                task.when = {
                    startAt: response.task.when?.startAt,
                    timezone: response.task.when?.timezone || "America/Los_Angeles",
                }
                task.reminder =
                    reminderOptions.value.find((option) => option.attribute === response.task.reminder)?.attribute ||
                    "minutes_30"
                task.shouldMarkAsMissedIfNotCompleted = response.task.shouldMarkAsMissedIfNotCompleted || false
                task.agent = {
                    _id: response.task.agent?._id ? response.task.agent?._id : currentUser.value._id,
                    fullName: response.task.agent?.fullName,
                }
                task.agentId = response.task.agent?._id ? response.task.agent?._id : currentUser.value._id
                task.type = taskTypes.value.find((option) => option.attribute === response.task.type)?.attribute || ""
                task.collection = {
                    _id: response.task.collection?._id,
                    displayName: response.task.collection?.displayName,
                }
                task.collectionId = response.task.collection?._id
                task.status = response.task.status
                task.notes = response.task.notes
                task.domainId = response.task.domainId || ""
                selectedDomain.value = domainOptions.value.find((option) => option._id === task?.domainId)?._id
                setQueueDateAndTime(getters.getNewDate(task.when.startAt))
            } catch (e) {
                console.log(`There was an error getting task with id: ${props.taskId}`, e)
            } finally {
                isTaskLoading.value = false
            }
        }
    }
)

watch(
    () => agencyStore.selectedDomain,
    (newValue) => {
        selectedDomain.value = newValue._id === "all" ? "" : newValue._id
    }
)
</script>
